/**
 * Guest KiTa demo: archetypal Eltern-Mail scenarios.
 * Relative dates so the Beispiel never looks expired; not a faux live inbox.
 */

import { PARENT_MAIL_LABEL } from "./scheduleLabels";
import { todayYmdZurich } from "./schoolAttention";
import { addDaysYmd } from "./heuteScheduleBuckets";

/** @typedef {"deadline" | "pack" | "schedule" | "rare_admin" | "other"} SchoolEmailDemoVariant */

/**
 * @param {string} todayYmd
 * @param {SchoolEmailDemoVariant | null | undefined} variant
 */
export function getSchoolEmailDemo(todayYmd = todayYmdZurich(), variant = "deadline") {
  const key = variant && VARIANT_BUILDERS[variant] ? variant : "deadline";
  return VARIANT_BUILDERS[key](todayYmd);
}

/** Default guest chat / /kita preview — Anmeldung + Elternabend. */
export function getDefaultSchoolEmailDemo(todayYmd = todayYmdZurich()) {
  return getSchoolEmailDemo(todayYmd, "deadline");
}

/**
 * Days until the next occurrence of `targetDow` (JS getDay: 0 Sun … 5 Fri).
 * If today is that weekday, skip to next week so the Beispiel stays upcoming.
 * @param {string} ymd
 * @param {number} targetDow
 */
export function daysUntilNextWeekday(ymd, targetDow) {
  const d = new Date(`${ymd}T12:00:00`);
  const delta = (targetDow - d.getDay() + 7) % 7;
  return delta === 0 ? 7 : delta;
}

/** @param {string} ymd */
function formatDemoDayMonth(ymd) {
  const d = new Date(`${ymd}T12:00:00`);
  return d.toLocaleDateString("de-CH", { day: "numeric", month: "numeric" });
}

/** @param {string} ymd */
function formatDemoWeekday(ymd) {
  const d = new Date(`${ymd}T12:00:00`);
  return d.toLocaleDateString("de-CH", { weekday: "long" });
}

/**
 * @param {string} todayYmd
 * @param {{ title: string, stakes?: string, taskTitle: string, eventTitle: string, eventTime?: string | null, taskOffset?: number, eventOffset?: number, sourceLabel?: string | null, quoteBuilder?: (dates: { taskDate: string, eventDate: string, eventTime: string | null }) => string | null }} spec
 */
function buildPair(todayYmd, spec) {
  const taskOffset = spec.taskOffset ?? 3;
  const eventOffset = spec.eventOffset ?? 10;
  const taskDate = addDaysYmd(todayYmd, taskOffset);
  const eventDate = addDaysYmd(todayYmd, eventOffset);
  const eventTime = spec.eventTime ?? null;
  const allDay = eventTime == null;
  const sourceQuote = spec.quoteBuilder
    ? spec.quoteBuilder({ taskDate, eventDate, eventTime })
    : spec.sourceQuote || null;

  return {
    receivedLabel: "Beispiel",
    headline: spec.title,
    stakes: spec.stakes || "",
    sourceLabel: spec.sourceLabel || null,
    sourceQuote,
    events: [
      {
        title: spec.taskTitle,
        date: taskDate,
        time: null,
        all_day: true,
        end_date: null,
        child: null,
        action_type: "task",
        is_actionable: true,
        confidence: "high",
      },
      {
        title: spec.eventTitle,
        date: eventDate,
        time: eventTime,
        all_day: allDay,
        end_date: null,
        child: null,
        action_type: "event",
        is_actionable: false,
        confidence: "high",
      },
    ],
  };
}

/** @type {Record<SchoolEmailDemoVariant, (todayYmd: string) => object>} */
const VARIANT_BUILDERS = {
  deadline: (todayYmd) =>
    buildPair(todayYmd, {
      title: "Elternabend — so erkennt Albis das",
      taskTitle: "Anmelden Elternabend",
      eventTitle: "Elternabend",
      eventTime: "19:00",
      taskOffset: daysUntilNextWeekday(todayYmd, 5),
      eventOffset: 20,
      sourceLabel: PARENT_MAIL_LABEL,
      quoteBuilder: ({ taskDate, eventDate }) =>
        `«Elternabend am ${formatDemoDayMonth(eventDate)}, 19 Uhr. Bitte bis ${formatDemoWeekday(taskDate)} anmelden.»`,
    }),
  pack: (todayYmd) =>
    buildPair(todayYmd, {
      title: "Schwimmen — so erkennt Albis das",
      stakes: "Ohne Sachen kein Mitmachen.",
      taskTitle: "Badi-Sachen mitbringen",
      eventTitle: "Schwimmnachmittag",
      taskOffset: 2,
      eventOffset: 2,
    }),
  schedule: (todayYmd) =>
    buildPair(todayYmd, {
      title: "Sonderzeiten — so erkennt Albis das",
      stakes: "Sonst steht jemand zur falschen Zeit vor der Tür.",
      taskTitle: "Abholen klären (wer holt ab)",
      eventTitle: "KiTa schliesst früher",
      eventTime: "15:00",
      taskOffset: 1,
      eventOffset: 1,
    }),
  rare_admin: (todayYmd) =>
    buildPair(todayYmd, {
      title: "Ferienhort — so erkennt Albis das",
      stakes: "Frist verpasst — Plätze sind weg.",
      taskTitle: "Ferienhort anmelden",
      eventTitle: "Ferienhort beginnt",
      taskOffset: 5,
      eventOffset: 21,
    }),
  other: (todayYmd) => VARIANT_BUILDERS.deadline(todayYmd),
};

/**
 * Demo events as chat-agenda rows (same chrome as live Termine & Fristen).
 * @param {object[]} events
 */
export function schoolEmailDemoToAgendaItems(events) {
  return (events || []).map((event, idx) => ({
    key: `demo-${idx}-${event.title || "item"}`,
    title: event.title,
    date: event.date,
    time: event.time || null,
    all_day: event.all_day !== false && !event.time,
    overdue: false,
    remindEnabled: false,
  }));
}
